import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Form, Card, Row, Col, notification, Popconfirm } from 'antd';
import { indexBy } from 'utils/utils2';
import GetLabel from 'DisplayFramework/components/GetLabel';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { PlusOutlined } from '@ant-design/icons';
import {
  AssetFeesResponse,
  deleteAssetFees,
  getAssetFeesListByAssetid,
  updateAssetFeesStatus,
} from 'services/assets';
import ButtonComponent from 'components/Button';
import { SwapOutlined } from '@ant-design/icons';
import { AuthContext } from 'components/Auth/Auth';
import DeleteButton from 'components/DeleteButton';
import ModalControllerNV from 'components/ModalControllerNV';
import AssetFeesModal from './AssetFeesModal';
import Value from 'components/Value';
import ThirdPartyModal from 'pages/ThirdParty/ThirdPartyDetail/ThirdPartyModal';
import Button from 'components/Button';
import moment from 'moment';
import { useForm } from 'antd/lib/form/Form';
import { toCurrency } from 'utils/utils';
import { useQuery } from 'react-query';
import { getFinAssetFeesListByAssetid } from 'services/finassets';
import { isNil } from 'lodash';

type AssetFeesProps = {
  assetId: number;
  currcode: string;
  phase: any;
};
const AssetFees = (props: AssetFeesProps) => {
  const { assetId, currcode,phase } = props;
  const { isInspect } = useContext(AuthContext);
  const [form] = useForm();
  const fieldName = 'assetFees';
  const initialValues = {
    assetid: assetId,
    feescategory: '',
    feescreationdate: '',
    feesdescription: '',
    feesdiscountamount: 0,
    feesdiscountrate: 0,
    feesid: -1,
    feesnetamount: 0,
    feesoriginalnetamount: 0,
    feesstatus: 'INIT',
    feesstatusdate: moment() as any,
    feestaxamount: 0,
    feestype: '',
    feesuserid: 0,
    feesvaluedate: '',
    fessgrossamount: 0,
    flagrebilling: false,
    picture1after: '',
    picture1before: '',
    picture2after: '',
    picture2before: '',
    picture3after: '',
    picture3before: '',
    taskid: 0,
    taxcode: '',
    taxrate: 0,
    tpidsupplier: 0,
  };


  const useGetAssetFees =  (
    assetId?: number,
    phase?: 'PROD' | 'PROPOSAL',
    enabled: boolean = true
  ) => {
  
    const assetfees = useQuery(
      ['getAssetFeesListByAssetid', assetId],
      () =>
        getAssetFeesListByAssetid({ assetid: assetId! }).then(
          (res) => Array.isArray(res.list) ? res.list : []
        ),
      {  enabled: !isNil(assetId) && phase === 'PROPOSAL' && enabled, }
    );
  
  
    const finAssetFees = useQuery(
      ['getFinAssetFeesListByAssetid', assetId],
      () =>
        getFinAssetFeesListByAssetid({ assetid: assetId! }).then(
          (res) => res.list || []
        ),
      {
        enabled: !isNil(assetId) && phase === 'PROD' && enabled,
      }
    );
  
    return phase === 'PROD' ? finAssetFees : assetfees;
  }

  const { data: fees, refetch } = useGetAssetFees(assetId, phase);


  useEffect(() => {
    refetch();
    if (fees) {
      form.setFieldsValue({
        [fieldName]: fees
      });
    }
  }, [fees, refetch]);
  

  const getName = useCallback(
    (name: string) => (record: AssetFeesResponse) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'feesid')[record?.feesid];
      return [fieldName, index, name];
    },
    [form, fees]
  );
  
  const columns = useMemo<DataGridColDef<AssetFeesResponse>[]>(
    () => [
      {
        dataIndex: 'feestypelabel',

        render: (index, record) => (
          <Form.Item name={getName('feestypelabel')(record)} noStyle>
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a
                  onClick={() => {
                    setVisible(true);
                  }}
                >
                  {record.feestypelabel}
                </a>
              )}
              modal={({ setVisible, visible }) => (
                <AssetFeesModal
                  onCancel={() => {
                    setVisible(false);
                  }}
                  visible={visible}
                  addorupdate="UPDATE"
                  currcode={currcode}
                  assetId={assetId}
                  record={record}
                  initialValues={initialValues}
                  refetch={refetch}
                />
              )}
            />
          </Form.Item>
        ),
        width: 140,
        fixed: 'left',
      },
      {
        dataIndex: 'feescategorylabel',
        render: (index, record) => (
          <Form.Item name={getName('feescategorylabel')(record)} noStyle>
            <Value />
          </Form.Item>
        ),
        width: 140,
      },

      {
        dataIndex: 'feesdescription',

        render: (index, record) => (
          <Form.Item name={getName('feesdescription')(record)} noStyle>
            <Value />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'suppliername',

        render: (index, record) => {
          return (
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a
                  onClick={() => {
                    setVisible(true);
                  }}
                >
                  {record.suppliername}
                </a>
              )}
              modal={({ setVisible, visible }) => (
                <ThirdPartyModal
                  tpId={record.tpidsupplier}
                  visible={visible}
                  onCancel={() => setVisible(false)}
                  onSuccess={() => setVisible(false)}
                  title={record.suppliername}
                  iscreate={false}
                />
              )}
            />
          );
        },
        width: 140,
      },
      {
        dataIndex: 'feesnetamount',

        render: (index, record) => (
          <Form.Item name={getName('feesnetamount')(record)} noStyle>
            <Value
              formatter={(value) =>
                toCurrency(
                  value,
                  currcode ?? localStorage.getItem('currcode') ?? ''
                )
              }
            />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'feestaxamount',

        render: (index, record) => (
          <Form.Item name={getName('feestaxamount')(record)} noStyle>
            <Value
              formatter={(value) =>
                toCurrency(
                  value,
                  currcode ?? localStorage.getItem('currcode') ?? ''
                )
              }
            />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'fessgrossamount',

        render: (index, record) => (
          <Form.Item name={getName('fessgrossamount')(record)} noStyle>
            <Value
              formatter={(value) =>
                toCurrency(
                  value,
                  currcode ?? localStorage.getItem('currcode') ?? ''
                )
              }
            />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'feesstatuslabel',

        render: (index, record) => (
          <Form.Item name={getName('feesstatuslabel')(record)} noStyle>
            <Value />
          </Form.Item>
        ),
        width: 140,
        fixed: 'right',
      },

      {
        key: 'actions',
        render: (record) => (
          <Row>
            <Col>
              <ButtonComponent
                icon={<SwapOutlined />}
                comcode={
                  record?.feesstatus === 'INIT'
                    ? 'Valid'
                    : record?.feesstatus === 'VALID'
                    ? 'Cancel'
                    : 'Valid'
                }
                style={{
                  width: 120,
                  height: 35,
                  marginLeft: 5,
                  marginRight: 5,
                  marginBottom: 5,
                }}
                type="primary"
                onClick={() =>
                  !isInspect &&
                  updateAssetFeesStatus({
                    feesid: Number(record?.feesid),
                    status:
                      record?.feesstatus === 'INIT'
                        ? 'VALID'
                        : record?.feesstatus === 'VALID'
                        ? 'CANCEL'
                        : 'VALID',
                  }).then((res) => {
                    notification.success({
                      message: res.message,
                    });
                    refetch();
                  })
                }
              />
            </Col>
            <Col>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteAssetFees({
                    feesid: record.feesid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </Col>
          </Row>
        ),
        width: 200,
        fixed: 'right',
      },
    ],
    [assetId, form, fees, getName, isInspect, refetch]
  );

  return (
    <Card
      title={
        <GetLabel
          label="assetFees"
          style={{ fontWeight: 'bold', paddingLeft: 15 }}
        />
      }
    >
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            return (
              <DataGrid
                hideTitle
                columns={columns}
                dataSource={getFieldValue(fieldName)}
                rowKey="compid"
                // hideFilters
                extraActions={
                  <ModalControllerNV
                    content={({ setVisible, visible }) => (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setVisible(true);
                        }}
                        comcode="add"
                      />
                    )}
                    modal={({ setVisible, visible }) => (
                      <AssetFeesModal
                        initialValues={initialValues}
                        onCancel={() => {
                          setVisible(false);
                        }}
                        visible={visible}
                        addorupdate="ADD"
                        currcode={currcode}
                        assetId={assetId}
                        refetch={refetch}
                      />
                    )}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </Card>
  );
};

export default AssetFees;
